import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PaymentSettingsSection = ({ settings, onUpdate, isEditing, onToggleEdit }) => {
  const [formData, setFormData] = useState(settings);
  const [showCardDetails, setShowCardDetails] = useState(false);

  const paymentMethods = [
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'bank_transfer', label: 'Bank Transfer (ACH)' },
    { value: 'wire_transfer', label: 'Wire Transfer' },
    { value: 'check', label: 'Check' }
  ];

  const billingCycles = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'bi_weekly', label: 'Bi-Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const cardTypes = [
    { value: 'visa', label: 'Visa' },
    { value: 'mastercard', label: 'Mastercard' },
    { value: 'amex', label: 'American Express' },
    { value: 'discover', label: 'Discover' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCardDetailsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      cardDetails: {
        ...prev?.cardDetails,
        [field]: value
      }
    }));
  };

  const handleBankDetailsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      bankDetails: {
        ...prev?.bankDetails,
        [field]: value
      }
    }));
  };

  const maskCardNumber = (number) => {
    if (!number) return '';
    return `**** **** **** ${number?.slice(-4)}`;
  };

  const handleSave = () => {
    onUpdate(formData);
    onToggleEdit();
  };

  const handleCancel = () => {
    setFormData(settings);
    setShowCardDetails(false);
    onToggleEdit();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
            <Icon name="CreditCard" size={20} className="text-error" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Payment Settings</h3>
            <p className="text-sm text-muted-foreground">Configure billing and payment preferences</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="default" size="sm" onClick={handleSave}>
                Save Changes
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={onToggleEdit} iconName="Edit2">
              Edit Settings
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-6">
        {/* Payment Method Selection */}
        <div>
          <Select
            label="Primary Payment Method"
            description="Choose your preferred payment method for lead purchases"
            options={paymentMethods}
            value={formData?.primaryPaymentMethod}
            onChange={(value) => handleInputChange('primaryPaymentMethod', value)}
            disabled={!isEditing}
            required
          />
        </div>

        {/* Credit Card Details */}
        {formData?.primaryPaymentMethod === 'credit_card' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">Credit Card Information</h4>
              {!isEditing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCardDetails(!showCardDetails)}
                  iconName={showCardDetails ? "EyeOff" : "Eye"}
                >
                  {showCardDetails ? 'Hide' : 'Show'} Details
                </Button>
              )}
            </div>
            
            {(isEditing || showCardDetails) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Cardholder Name"
                  type="text"
                  value={formData?.cardDetails?.holderName || ''}
                  onChange={(e) => handleCardDetailsChange('holderName', e?.target?.value)}
                  disabled={!isEditing}
                  required
                />
                <Select
                  label="Card Type"
                  options={cardTypes}
                  value={formData?.cardDetails?.type || ''}
                  onChange={(value) => handleCardDetailsChange('type', value)}
                  disabled={!isEditing}
                  required
                />
                <Input
                  label="Card Number"
                  type="text"
                  value={isEditing ? formData?.cardDetails?.number || '' : maskCardNumber(formData?.cardDetails?.number)}
                  onChange={(e) => handleCardDetailsChange('number', e?.target?.value)}
                  disabled={!isEditing}
                  placeholder="1234 5678 9012 3456"
                  required
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Expiry Date"
                    type="text"
                    value={formData?.cardDetails?.expiry || ''}
                    onChange={(e) => handleCardDetailsChange('expiry', e?.target?.value)}
                    disabled={!isEditing}
                    placeholder="MM/YY"
                    required
                  />
                  <Input
                    label="CVV"
                    type="text"
                    value={formData?.cardDetails?.cvv || ''}
                    onChange={(e) => handleCardDetailsChange('cvv', e?.target?.value)}
                    disabled={!isEditing}
                    placeholder="123"
                    required
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bank Transfer Details */}
        {formData?.primaryPaymentMethod === 'bank_transfer' && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Bank Account Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Account Holder Name"
                type="text"
                value={formData?.bankDetails?.accountHolderName || ''}
                onChange={(e) => handleBankDetailsChange('accountHolderName', e?.target?.value)}
                disabled={!isEditing}
                required
              />
              <Input
                label="Bank Name"
                type="text"
                value={formData?.bankDetails?.bankName || ''}
                onChange={(e) => handleBankDetailsChange('bankName', e?.target?.value)}
                disabled={!isEditing}
                required
              />
              <Input
                label="Routing Number"
                type="text"
                value={formData?.bankDetails?.routingNumber || ''}
                onChange={(e) => handleBankDetailsChange('routingNumber', e?.target?.value)}
                disabled={!isEditing}
                required
              />
              <Input
                label="Account Number"
                type="text"
                value={formData?.bankDetails?.accountNumber || ''}
                onChange={(e) => handleBankDetailsChange('accountNumber', e?.target?.value)}
                disabled={!isEditing}
                required
              />
            </div>
          </div>
        )}

        {/* Billing Configuration */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-foreground">Billing Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Billing Cycle"
              options={billingCycles}
              value={formData?.billingCycle}
              onChange={(value) => handleInputChange('billingCycle', value)}
              disabled={!isEditing}
              required
            />
            <Input
              label="Spending Limit ($)"
              type="number"
              value={formData?.spendingLimit || ''}
              onChange={(e) => handleInputChange('spendingLimit', parseFloat(e?.target?.value))}
              disabled={!isEditing}
              min="0"
              step="100"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Low Balance Alert ($)"
              type="number"
              value={formData?.lowBalanceAlert || ''}
              onChange={(e) => handleInputChange('lowBalanceAlert', parseFloat(e?.target?.value))}
              disabled={!isEditing}
              min="0"
              step="50"
            />
            <Input
              label="Auto-Reload Amount ($)"
              type="number"
              value={formData?.autoReloadAmount || ''}
              onChange={(e) => handleInputChange('autoReloadAmount', parseFloat(e?.target?.value))}
              disabled={!isEditing}
              min="0"
              step="100"
            />
          </div>
        </div>

        {/* Billing Preferences */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">Billing Preferences</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Checkbox
              label="Auto-reload when balance is low"
              checked={formData?.autoReload || false}
              onChange={(e) => handleInputChange('autoReload', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Email billing notifications"
              checked={formData?.emailNotifications || false}
              onChange={(e) => handleInputChange('emailNotifications', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="SMS spending alerts"
              checked={formData?.smsAlerts || false}
              onChange={(e) => handleInputChange('smsAlerts', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Detailed transaction reports"
              checked={formData?.detailedReports || false}
              onChange={(e) => handleInputChange('detailedReports', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* Current Balance & Status */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-foreground">Account Balance</h4>
            <div className="flex items-center space-x-2">
              <Icon name="DollarSign" size={16} className="text-success" />
              <span className="text-lg font-bold text-success">$1,247.50</span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">This Month</p>
              <p className="font-medium text-foreground">$892.25</p>
            </div>
            <div>
              <p className="text-muted-foreground">Last 7 Days</p>
              <p className="font-medium text-foreground">$234.75</p>
            </div>
            <div>
              <p className="text-muted-foreground">Avg. Daily</p>
              <p className="font-medium text-foreground">$33.54</p>
            </div>
            <div>
              <p className="text-muted-foreground">Next Billing</p>
              <p className="font-medium text-foreground">Aug 25, 2024</p>
            </div>
          </div>
        </div>

        {/* Security & Compliance */}
        <div className="flex items-center justify-between p-4 bg-success/10 rounded-lg">
          <div className="flex items-center space-x-3">
            <Icon name="Shield" size={20} className="text-success" />
            <div>
              <p className="text-sm font-medium text-success">PCI DSS Compliant</p>
              <p className="text-xs text-muted-foreground">Your payment data is encrypted and secure</p>
            </div>
          </div>
          <Button variant="outline" size="sm" iconName="ExternalLink">
            Security Details
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettingsSection;